import React from "react";
import BackButtom from "../../../components/backButton";
import DataGrid, { ItemField } from "../../../components/datagrid";
import ButtonRound from "../../../components/button-action";
import Icons from "../../../components/icons-helper";
import { theme } from "../../../styles/theme";
import {
  AdapterConfig,
  AdapterPreviewModel,
  AdapterSimulateModel,
  CityAdapterConfigModel,
} from "../../../../domain/models/city-adapter-config";
import { CityRuleCountModel } from "../../../../domain/models/service-taxation-code";
import ConfigEditor from "./components/config-editor";
import * as S from "./styles";

type Props = {
  // Busca de cidades
  cities: CityRuleCountModel[];
  citySearch: string;
  cityLoading: boolean;
  cityTotalPages: number;
  onCitySearchChange: (search: string) => void;
  onCityPageChange: (page: number) => void;
  onSelectCity: (city: CityRuleCountModel) => void;
  // Cidade selecionada
  selectedCity: CityRuleCountModel | null;
  selectedLayout: string;
  onLayoutChange: (layout: string) => void;
  existingConfig: CityAdapterConfigModel | null;
  createMode: boolean;
  // Edição
  editMode: "form" | "json";
  onEditModeChange: (mode: "form" | "json") => void;
  editConfig: AdapterConfig;
  onEditConfigChange: (config: AdapterConfig) => void;
  editAtivo: boolean;
  onEditAtivoChange: (value: boolean) => void;
  onSave: () => void;
  onDelete?: () => void;
  onBack: () => void;
  saving: boolean;
  // Preview
  preview: AdapterPreviewModel | null;
  previewLoading: boolean;
  previewError: string | null;
  // Simulação
  simulation: AdapterSimulateModel | null;
  simulationLoading: boolean;
  onSimulate: () => void;
  // Info de quais cidades têm config
  citiesWithConfig: string[];
};

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return "-";
  // API retorna data sem timezone (ex: 2026-08-11T13:19:03) já no horário local
  // Extraímos os componentes diretamente para evitar conversão de timezone
  const [datePart, timePart] = dateStr.split("T");
  if (!datePart) return "-";
  const [year, month, day] = datePart.split("-");
  const [hour, minute] = (timePart || "").split(":");
  if (!hour || !minute) return `${day}/${month}/${year}`;
  return `${day}/${month}/${year}, ${hour}:${minute}`;
};

const CityAdapterConfigPage: React.FC<Props> = ({
  cities,
  citySearch,
  cityLoading,
  cityTotalPages,
  onCitySearchChange,
  onCityPageChange,
  onSelectCity,
  selectedCity,
  selectedLayout,
  onLayoutChange,
  existingConfig,
  createMode,
  editMode,
  onEditModeChange,
  editConfig,
  onEditConfigChange,
  editAtivo,
  onEditAtivoChange,
  onSave,
  onDelete,
  onBack,
  saving,
  preview,
  previewLoading,
  previewError,
  simulation,
  simulationLoading,
  onSimulate,
  citiesWithConfig,
}) => {
  // --- Tela de edição (cidade selecionada) ---
  if (selectedCity) {
    return (
      <S.Wrapper>
        <S.BackHeader>
          <S.BackButton type="button" onClick={onBack}>← Voltar</S.BackButton>
          <S.PageTitle>
            {selectedCity.name} ({selectedCity.ibge})
          </S.PageTitle>
          <S.LayoutSelect value={selectedLayout} onChange={(e) => onLayoutChange(e.target.value)}>
            <option value="MUNICIPAL">Municipal</option>
            <option value="NATIONAL">Nacional</option>
          </S.LayoutSelect>
          {existingConfig && (
            <S.Badge $active={editAtivo}>{editAtivo ? "Ativo" : "Inativo"}</S.Badge>
          )}
          {createMode && (
            <S.Badge $active={false}>Sem configuração</S.Badge>
          )}
        </S.BackHeader>

        {/* Meta: toggle ativo + datas */}
        <S.MetaInfo>
          {existingConfig && (
            <>
              <label htmlFor="ativo-toggle" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#fff", fontFamily: "Poppins, sans-serif", cursor: "pointer" }}>
                <input id="ativo-toggle" type="checkbox" checked={editAtivo} onChange={(e) => onEditAtivoChange(e.target.checked)} />
                {editAtivo ? "Ativo" : "Inativo"}
              </label>
              <span>Criado em: {formatDate(existingConfig.createdAt)}</span>
              <span>Última alteração: {formatDate(existingConfig.updatedAt)}</span>
            </>
          )}
        </S.MetaInfo>

        <ConfigEditor
          createMode={createMode}
          editMode={editMode}
          onEditModeChange={onEditModeChange}
          config={editConfig}
          onConfigChange={onEditConfigChange}
          onSave={onSave}
          onDelete={onDelete}
          saving={saving}
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          simulation={simulation}
          simulationLoading={simulationLoading}
          onSimulate={onSimulate}
        />
      </S.Wrapper>
    );
  }

  // --- Tela de busca de cidades ---
  const cityFields: ItemField[] = [
    {
      title: "IBGE",
      key: "ibge",
      renderItem: (data: CityRuleCountModel) => data.ibge,
    },
    {
      title: "Cidade",
      key: "name",
      renderItem: (data: CityRuleCountModel) => data.name,
    },
    {
      title: "UF",
      key: "uf",
      renderItem: (data: CityRuleCountModel) => data.uf || "-",
    },
    {
      title: "Config",
      key: "hasConfig",
      renderItem: (data: CityRuleCountModel) => (
        <S.Badge $active={citiesWithConfig.includes(data.ibge)}>
          {citiesWithConfig.includes(data.ibge) ? "Configurado" : "Sem config"}
        </S.Badge>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      renderItem: (data: CityRuleCountModel) => (
        <ButtonRound
          backgroundColor={theme.colors.darkBlue}
          onClick={() => onSelectCity(data)}
          title="Configurar"
          color={theme.colors.lightBlue}
        >
          <Icons height={15} width={15} name="eye" color={theme.colors.primary} />
        </ButtonRound>
      ),
    },
  ];

  return (
    <S.Wrapper>
      <BackButtom text="Adapter Dinâmico NFSe — Configuração por Cidade" />
      <S.InfoBox>
        Configura transformações no JSON enviado ao PlugNotas por município. Permite definir valores fixos, renomear chaves, aplicar regras de formatação e remover campos — separadamente para layout Municipal e Nacional.
      </S.InfoBox>
      <S.CardWrapper>
        <S.SearchInput
          type="text"
          placeholder="Buscar por IBGE (7 díg), CEP (8 díg) ou nome da cidade..."
          value={citySearch}
          onChange={(e) => onCitySearchChange(e.target.value)}
        />
      </S.CardWrapper>
      <DataGrid
        loading={cityLoading}
        fields={cityFields}
        data={cities}
        totalPages={cityTotalPages}
        changePage={onCityPageChange}
      />
    </S.Wrapper>
  );
};

export default CityAdapterConfigPage;
